import { useCallback, useEffect, useState } from "preact/hooks";
import type { Message } from "./api.ts";

/**
 * The digits staff hand out, in dialing order. 0 is missing on purpose: it is
 * the operator line, which the phone builds from the directory itself.
 */
export const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

/** A recording held in memory between the record screen and the review screen. */
export interface Recorded {
  blob: Blob;
  url: string;
  contentType: string;
  seconds: number;
}

export type View =
  | { name: "home" }
  | { name: "setup"; digit: string }
  | { name: "number"; digit: string; category: string }
  | { name: "add"; digit: string; category: string }
  | { name: "record"; digit: string; category: string }
  | { name: "review"; digit: string; category: string; recording: Recorded }
  /** `editing` reopens a stored typed message in place of writing a new one. */
  | { name: "type"; digit: string; category: string; editing?: Message }
  | { name: "allowlist" }
  | { name: "voices" };

export interface Navigation {
  view: View;
  push: (view: View) => void;
  /** Swaps the top of the stack without adding a history entry. */
  replace: (view: View) => void;
  back: () => void;
  /** Back to the nearest screen of that kind, however many steps that is. */
  backTo: (name: View["name"]) => void;
}

function depthOf(state: unknown): number {
  return typeof state === "object" && state !== null && "depth" in state
    ? Number((state as { depth: unknown }).depth) || 0
    : 0;
}

/**
 * The view stack, kept in step with browser history so the back button and the
 * phone's back gesture do what the back chevron does. Only the depth goes into
 * `history.state` — a view can hold a recording, which is not something history
 * can store.
 *
 * Sign-in strips the OAuth code from the address bar with `replaceState`
 * before this ever mounts, so the two never collide.
 */
export function useNavigation(initial: View): Navigation {
  const [stack, setStack] = useState<View[]>([initial]);

  useEffect(() => {
    window.history.replaceState({ depth: 0 }, "");
    function onPop(event: PopStateEvent): void {
      const depth = depthOf(event.state);
      setStack((current) => current.slice(0, Math.max(1, depth + 1)));
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const push = useCallback((view: View) => {
    setStack((current) => {
      window.history.pushState({ depth: current.length }, "");
      return [...current, view];
    });
  }, []);

  const replace = useCallback((view: View) => {
    setStack((current) => [...current.slice(0, -1), view]);
  }, []);

  const back = useCallback(() => window.history.back(), []);

  const backTo = useCallback((name: View["name"]) => {
    setStack((current) => {
      const target = current.map((view) => view.name).lastIndexOf(name);
      if (target >= 0 && target < current.length - 1) {
        window.history.go(target - (current.length - 1));
      }
      // `popstate` trims the stack; returning it unchanged keeps the two in step.
      return current;
    });
  }, []);

  const view = stack[stack.length - 1] ?? initial;
  return { view, push, replace, back, backTo };
}
